import { useEffect, useState } from 'react'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import { observer } from 'mobx-react'

import { Account } from 'Modules/account/pages/Account.page'
import { Contact, ContactLanding } from 'Modules/contact/pages'
import { EmployeeDashboardDefault } from 'Modules/dashboard/components/EmployeeDashboardDefault/EmployeeDashboardDefault'
import { DataPrivacy } from 'Modules/dataPrivacy/pages'
import {
  Offer,
  OfferAccepted,
  OfferDeclined,
  OfferRecreated,
  PaymentFailed,
  PurchaseDevice,
  PurchaseSuccessful,
} from 'Modules/purchase/pages'
import { PublicRequestReturnLabel } from 'Modules/returns/pages'
import { Sitemap } from 'Modules/sitemap/pages'

import { useStore } from 'Stores/index'
import { NavigationConfigTypeEnum } from 'Stores/navigationStore/navigationStore.types'

import { useAuthToken, useIsServiceApp, usePermissions } from 'Hooks'

import { hasPermission } from 'Utilities'

import { AuthorisedLayout, UnauthorisedLayout } from '../layouts'
import { ProtectedRoute } from './ProtectedRoute'
import { PublicRoute } from './PublicRoute'
import { RedirectWithParamsAndQuery } from './RedirectWithParamsAndQuery'
import { ROUTER_CONFIG } from './Router.config'
import { GLOBAL_ROUTE_PATHS, ROUTER_REDIRECTS } from './Router.constants'
import type { RouterState } from './Router.types'
import { mapRouteWithChildrenToRoute } from './Router.utils'

export const Router = observer(() => {
  const { aclsStore, navigationStore } = useStore()
  const { allowedAcls } = aclsStore
  const {
    mainNavigationConfig: { url },
    setMainNavigationConfig,
  } = navigationStore
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const authToken = useAuthToken()
  const isServiceApp = useIsServiceApp()
  const permissions = usePermissions()
  const [routerState, setRouterState] = useState<RouterState>({
    currentPath: '/',
    prevPath: '',
  })

  const { publicRoutes } = ROUTER_CONFIG
  const protectedRoutes = [
    ...mapRouteWithChildrenToRoute(ROUTER_CONFIG.protectedRoutes.organisation),
    ...mapRouteWithChildrenToRoute(ROUTER_CONFIG.protectedRoutes.employee),
  ]

  useEffect(() => {
    const checkPathACLS = () => {
      const pathParts = pathname.split('/')
      const isOrg = pathParts[1].includes('organisation')
      const isEmployee = pathParts[1].includes('employee')

      const isAllowedOrg =
        isOrg &&
        !!allowedAcls?.organisations.organisations?.some(
          org => org.id.toString() === pathParts[2],
        )

      const isAllowedEmployee =
        isEmployee &&
        !!allowedAcls?.allowedEmployees.employees?.some(
          employee => employee.id.toString() === pathParts[2],
        )

      const newId = pathParts[2]
      const newUrl = `/${pathParts[1]}/${newId}`

      if (
        isAllowedEmployee &&
        !hasPermission(permissions, permissions.global.dropdownAclEmployees)
      ) {
        localStorage.setItem('lastSelected', 'organisation')
        localStorage.setItem(
          'lastSelectedId',
          allowedAcls?.organisations.organisations?.[0].id || '',
        )

        return
      }

      if (!allowedAcls && (isOrg || isEmployee)) {
        localStorage.setItem(
          'lastSelected',
          isOrg ? 'organisation' : 'employee',
        )

        localStorage.setItem('lastSelectedId', pathParts[2])

        return
      }

      if ((isAllowedOrg || isAllowedEmployee) && newUrl !== url) {
        setMainNavigationConfig({
          id: newId,
          type: isAllowedOrg
            ? NavigationConfigTypeEnum.Organisation
            : NavigationConfigTypeEnum.Employee,
          url: newUrl,
        })

        return
      }

      if (
        !window.location.hash.includes(url) &&
        !window.location.hash.includes(GLOBAL_ROUTE_PATHS.public.contact) &&
        !window.location.hash.includes(GLOBAL_ROUTE_PATHS.public.dataPrivacy) &&
        !window.location.hash.includes(GLOBAL_ROUTE_PATHS.private.account) &&
        !window.location.hash.includes(GLOBAL_ROUTE_PATHS.public.offer) &&
        !window.location.hash.includes(
          GLOBAL_ROUTE_PATHS.public.offerAccepted,
        ) &&
        !window.location.hash.includes(
          GLOBAL_ROUTE_PATHS.public.offerDeclined,
        ) &&
        !window.location.hash.includes(
          GLOBAL_ROUTE_PATHS.public.offerRecreated,
        ) &&
        !window.location.hash.includes(
          GLOBAL_ROUTE_PATHS.public.purchaseSuccessful,
        ) &&
        !window.location.hash.includes(
          GLOBAL_ROUTE_PATHS.public.paymentFailed,
        ) &&
        !window.location.hash.includes(
          GLOBAL_ROUTE_PATHS.public.purchaseDevice.replace(':uuid', ''),
        ) &&
        !window.location.hash.includes(GLOBAL_ROUTE_PATHS.public.sitemap) &&
        !window.location.hash.includes(
          GLOBAL_ROUTE_PATHS.public.requestReturnLabel,
        )
      ) {
        navigate(url, { replace: true })
      }
    }

    checkPathACLS()
  }, [
    allowedAcls,
    navigate,
    pathname,
    url,
    setMainNavigationConfig,
    permissions,
  ])

  useEffect(() => {
    // MANAGE ROUTER STATE
    if (routerState.currentPath === pathname) {
      return
    }

    setRouterState(({ currentPath }) => ({
      currentPath: pathname,
      prevPath: currentPath,
    }))
  }, [pathname, routerState.currentPath])

  useEffect(() => {
    // EXECUTE CUSTOM ACTIONS
    ROUTER_CONFIG.customRouterActions.onRouteChange.forEach(action => {
      if (!action) {
        return
      }

      action(routerState.prevPath, routerState.currentPath)
    })
  }, [routerState])

  return (
    <Routes>
      {ROUTER_REDIRECTS.map(({ from, to }) => (
        <Route
          key={to}
          element={<RedirectWithParamsAndQuery to={to} />}
          path={from}
        />
      ))}

      {protectedRoutes.map(route => (
        <Route
          {...route}
          key={route.path}
          element={<ProtectedRoute {...route} />}
        />
      ))}

      {publicRoutes.map(({ path, ...rest }) => {
        const formattedPath = `/${path}`

        return (
          <Route
            key={formattedPath}
            path={formattedPath}
            {...rest}
            element={<PublicRoute path={formattedPath} {...rest} />}
          />
        )
      })}

      <Route
        element={
          authToken ? (
            <AuthorisedLayout>
              <Contact />
            </AuthorisedLayout>
          ) : (
            <UnauthorisedLayout>
              <ContactLanding />
            </UnauthorisedLayout>
          )
        }
        path={GLOBAL_ROUTE_PATHS.public.contact}
      />

      <Route
        element={
          authToken ? (
            <AuthorisedLayout>
              <Sitemap />
            </AuthorisedLayout>
          ) : (
            <UnauthorisedLayout>
              <Sitemap isUnauthorized />
            </UnauthorisedLayout>
          )
        }
        path={GLOBAL_ROUTE_PATHS.public.sitemap}
      />

      <Route
        element={
          <UnauthorisedLayout>
            <PublicRequestReturnLabel />
          </UnauthorisedLayout>
        }
        path={GLOBAL_ROUTE_PATHS.public.requestReturnLabel}
      />

      <Route
        element={
          <UnauthorisedLayout>
            <PublicRequestReturnLabel />
          </UnauthorisedLayout>
        }
        path={GLOBAL_ROUTE_PATHS.public.requestReturnLabelHash}
      />

      <Route
        element={
          <UnauthorisedLayout>
            <Offer />
          </UnauthorisedLayout>
        }
        path={GLOBAL_ROUTE_PATHS.public.offer}
      />

      <Route
        element={
          <UnauthorisedLayout>
            <OfferAccepted />
          </UnauthorisedLayout>
        }
        path={GLOBAL_ROUTE_PATHS.public.offerAccepted}
      />

      <Route
        element={
          <UnauthorisedLayout>
            <OfferDeclined />
          </UnauthorisedLayout>
        }
        path={GLOBAL_ROUTE_PATHS.public.offerDeclined}
      />

      <Route
        element={
          <UnauthorisedLayout>
            <OfferRecreated />
          </UnauthorisedLayout>
        }
        path={GLOBAL_ROUTE_PATHS.public.offerRecreated}
      />

      <Route
        element={
          <UnauthorisedLayout>
            <PurchaseSuccessful />
          </UnauthorisedLayout>
        }
        path={GLOBAL_ROUTE_PATHS.public.purchaseSuccessful}
      />

      <Route
        element={
          <UnauthorisedLayout>
            <PaymentFailed />
          </UnauthorisedLayout>
        }
        path={GLOBAL_ROUTE_PATHS.public.paymentFailed}
      />

      <Route
        element={
          <UnauthorisedLayout>
            <PurchaseDevice />
          </UnauthorisedLayout>
        }
        path={GLOBAL_ROUTE_PATHS.public.purchaseDevice}
      />

      {isServiceApp && (
        <Route
          element={
            authToken ? (
              <AuthorisedLayout>
                <Account />
              </AuthorisedLayout>
            ) : (
              <UnauthorisedLayout>
                <EmployeeDashboardDefault />
              </UnauthorisedLayout>
            )
          }
          path={GLOBAL_ROUTE_PATHS.private.account}
        />
      )}

      <Route
        element={
          authToken ? (
            <AuthorisedLayout>
              <DataPrivacy />
            </AuthorisedLayout>
          ) : (
            <UnauthorisedLayout showFooter>
              <DataPrivacy />
            </UnauthorisedLayout>
          )
        }
        path={GLOBAL_ROUTE_PATHS.public.dataPrivacy}
      />

      {authToken ? (
        <Route element={<Navigate replace to={url} />} path="/" />
      ) : (
        <Route
          element={<Navigate replace to={GLOBAL_ROUTE_PATHS.public.login} />}
          path="*"
        />
      )}

      <Route element={<RedirectWithParamsAndQuery to={url} />} path="/*" />
    </Routes>
  )
})
